import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { MIN_LEN } from '@/constants';
import { getCustomerInitials } from '@/utils/user.utils';
import {
  selectManageAccessDraft,
  selectManageAccessExternalUsers,
  selectManageAccessInternalUsers,
} from '@/store/selectors/manageAccess';
import { setManageAccessDraftField } from '@/store/slices/manageAccess/manageAccessSlice';

const NEW_EXTERNAL_INDEX_SEGMENT = 2;

type ReviewPersonRole = 'Owner' | 'Member';

interface ReviewPersonRowModel {
  identifier: string;
  name: string;
  email: string;
  initials: string;
  role: ReviewPersonRole;
  isRemovable: boolean;
}

interface ReviewPeopleGroupModel {
  title: string;
  people: ReviewPersonRowModel[];
}

function countPeople(groups: ReviewPeopleGroupModel[]): number {
  return groups.reduce((accumulator, group) => accumulator + group.people.length, MIN_LEN);
}

export function useStepManageAccessReview() {
  const dispatch = useAppDispatch();
  const draftState = useAppSelector(selectManageAccessDraft);
  const internalUsers = useAppSelector(selectManageAccessInternalUsers);
  const externalUsers = useAppSelector(selectManageAccessExternalUsers);

  const internalUsersByIdentifier = useMemo(() => {
    const mapping = new Map<string, (typeof internalUsers)[number]>();
    internalUsers.forEach((internalUser) => {
      mapping.set(internalUser.id, internalUser);
    });
    return mapping;
  }, [internalUsers]);

  const externalUsersByIdentifier = useMemo(() => {
    const mapping = new Map<string, (typeof externalUsers)[number]>();
    externalUsers.forEach((externalUser) => {
      mapping.set(externalUser.id, externalUser);
    });
    return mapping;
  }, [externalUsers]);

  const groups = useMemo<ReviewPeopleGroupModel[]>(() => {
    const ownerRows: ReviewPersonRowModel[] = draftState.ownerIds
      .map((ownerIdentifier) => internalUsersByIdentifier.get(ownerIdentifier))
      .filter((internalUser): internalUser is NonNullable<typeof internalUser> => internalUser !== undefined)
      .map((internalUser) => ({
        identifier: `owner-${internalUser.id}`,
        name: internalUser.name,
        email: internalUser.email,
        initials: getCustomerInitials(internalUser.name),
        role: 'Owner' as const,
        isRemovable: false,
      }));

    const ownerIdentifiersSet = new Set(draftState.ownerIds);

    const memberRows: ReviewPersonRowModel[] = draftState.internalParticipantIds
      .filter((participantIdentifier) => !ownerIdentifiersSet.has(participantIdentifier))
      .map((participantIdentifier) => internalUsersByIdentifier.get(participantIdentifier))
      .filter((internalUser): internalUser is NonNullable<typeof internalUser> => internalUser !== undefined)
      .map((internalUser) => ({
        identifier: `member-${internalUser.id}`,
        name: internalUser.name,
        email: internalUser.email,
        initials: getCustomerInitials(internalUser.name),
        role: 'Member' as const,
        isRemovable: false,
      }));

    const externalContactRows: ReviewPersonRowModel[] = draftState.externalParticipantIds
      .map((externalIdentifier) => externalUsersByIdentifier.get(externalIdentifier))
      .filter((externalUser): externalUser is NonNullable<typeof externalUser> => externalUser !== undefined)
      .map((externalUser) => ({
        identifier: `external-contact-${externalUser.id}`,
        name: externalUser.name,
        email: externalUser.email,
        initials: getCustomerInitials(externalUser.name),
        role: 'Member' as const,
        isRemovable: true,
      }));

    const newExternalRows: ReviewPersonRowModel[] = draftState.newExternalUsers.map(
      (newExternalUser, externalIndex) => ({
        identifier: `new-external-${String(externalIndex)}-${newExternalUser.email}`,
        name: newExternalUser.name,
        email: newExternalUser.email,
        initials: getCustomerInitials(newExternalUser.name),
        role: 'Member' as const,
        isRemovable: true,
      })
    );

    const internalGroupPeople = [...ownerRows, ...memberRows];
    const externalGroupPeople = [...externalContactRows, ...newExternalRows];

    const nextGroups: ReviewPeopleGroupModel[] = [];

    if (internalGroupPeople.length > MIN_LEN) nextGroups.push({ title: 'Internal Staff', people: internalGroupPeople });
    if (externalGroupPeople.length > MIN_LEN) nextGroups.push({ title: 'External People', people: externalGroupPeople });

    return nextGroups;
  }, [
    draftState.externalParticipantIds,
    draftState.internalParticipantIds,
    draftState.newExternalUsers,
    draftState.ownerIds,
    externalUsersByIdentifier,
    internalUsersByIdentifier,
  ]);

  const totalPeopleCount = countPeople(groups);

  const handleRemovePerson = useCallback(
    (personIdentifier: string): void => {
      if (personIdentifier.startsWith('external-contact-')) {
        const contactId = personIdentifier.replace('external-contact-', '');
        dispatch(
          setManageAccessDraftField({
            key: 'externalParticipantIds',
            value: draftState.externalParticipantIds.filter(
              (existingContactId) => existingContactId !== contactId
            ),
          })
        );
      } else if (personIdentifier.startsWith('new-external-')) {
        const indexSegment = personIdentifier.split('-')[NEW_EXTERNAL_INDEX_SEGMENT];
        const targetIndex = Number(indexSegment);
        dispatch(
          setManageAccessDraftField({
            key: 'newExternalUsers',
            value: draftState.newExternalUsers.filter(
              (_, currentIndex) => currentIndex !== targetIndex
            ),
          })
        );
      }
    },
    [dispatch, draftState.externalParticipantIds, draftState.newExternalUsers]
  );

  return {
    state: {
      groups,
      totalPeopleCount,
    },
    handlers: {
      handleRemovePerson,
    },
  };
}
